import type {
  LegacyAtProp,
  LegacyOffsetFromPropBack,
  LegacyRoomLayoutLoad,
  LegacyRoomWidthParamAtPropAxis,
} from "building-types/electrical/electricalLegacyRoomLayoutTypes";
import {
  LegacyElectricalPositionType,
  LegacyPropPositionType,
  LegacyRoomEdgePositionType,
} from "building-types/electrical/electricalLegacyRoomLayoutTypesEnum";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import type { LoadDescriptor } from "building-types/electrical/electricalTypes";
import { LoadSymbol } from "building-types/electrical/electricalTypesEnum";
import type { Load } from "building-types/electrical/generatedElectricalTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { getLoadCatalog } from "doc/getLoadCatalog";
import type { MergedPropLibrary } from "doc/propTools";
import { normalizeEntityPointToPolyLine2 } from "doc/rooms/normalizeEntityPointToPolyLine2";
import { normalizeEntityPointToPolyLine2ForceXToRoomWidth } from "doc/rooms/normalizeEntityPointToPolyLine2ForceXToRoomWidth";
import { propBBAdjustments } from "doc/utils/propTools";
import { line2MidPt } from "geom/line2";
import {
  polyLine2Centroid,
  polyLine2Edge,
  polyLine2EdgeEval,
  polyLine2EdgeEvalNegatedOffset,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2Length,
  polyLine2OppositePt,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Normalize, vec2Perp, vec2Rotate, vec2Sub } from "geom/vec2";
import { loadTypeFromSlots } from "../loadTypeFromSlots";
import { reverseParam } from "../reverseParam";

// Concentric circles only, so rotating one about its own center is visually inert and a non-default
// `normal` can steer the plan label freely. FRAMED_CIRCLE and CEILING_LIGHT are excluded: their
// square frame and crosshair are only symmetric at 90° increments and would visibly tilt.
const ROUND_CEILING_LIGHT_SYMBOLS = new Set<LoadSymbol>([
  LoadSymbol.CIRCLE_4,
  LoadSymbol.CIRCLE_7,
  LoadSymbol.CIRCLE_8,
  LoadSymbol.CIRCLE_DL_45_15,
  LoadSymbol.CIRCLE_DL_60_40,
  LoadSymbol.CIRCLE_DL_12,
]);

/**
 * Deterministic label direction for a ceiling light with no real wall or prop to anchor to, so the
 * label offsets away from the middle of the room rather than always the same fixed corner. Falls
 * back to `[0, 1]` for a degenerate outline or a point sitting on the centroid.
 */
function directionAwayFromRoomCentroid(outline: PolyLine2, x: number, y: number): [number, number] {
  const centroid = polyLine2Centroid(outline);
  if (!centroid) {
    return [0, 1];
  }
  const [dx, dy] = vec2Sub(x, y, centroid[0], centroid[1]);
  if (dx === 0 && dy === 0) {
    return [0, 1];
  }
  const [nX, nY] = vec2Normalize(dx, dy);
  return [nX, nY];
}

export async function getLoadsFromRoomLayoutLegacy(
  loads: LegacyRoomLayoutLoad[],
  elecSlots: ElectricalSlots,
  room: Room,
  roomProps: Prop[],
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  electricalItemCatalogSettings: ElectricalItemCatalogSettings,
  edgeIds: string[],
  roomOutlines: Table<RoomOutlines>,
  mirror: boolean,
): Promise<Load[]> {
  const outline = roomOutlines[room.id].INSIDE_OF_MASS_IGNORE_VOID ?? room.outline;
  const loadItems: Load[] = [];
  if (loads) {
    for (const load of loads) {
      const { loadType: rawloadType, loadTypeSlot, queryPoint: queryPts } = load;
      const loadType = rawloadType ? rawloadType : loadTypeFromSlots(loadTypeSlot, elecSlots);
      const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
      const loadDescriptor = loadCatalog[loadType];
      if (!loadDescriptor) {
        continue;
      }

      for (const queryPt of queryPts) {
        switch (queryPt.type) {
          case LegacyElectricalPositionType.PROP: {
            const { propPosition, propId } = queryPt;
            switch (propPosition.type) {
              case LegacyPropPositionType.AT_PROP: {
                await pointOnProp(
                  propPosition,
                  roomProps,
                  propId,
                  propCatalog,
                  mergedPropLibrary,
                  loadItems,
                  loadType,
                  loadDescriptor,
                  room.level.id,
                  room,
                );
                break;
              }
              case LegacyPropPositionType.OFFSET_FROM_PROP_BACK: {
                await pointOffsetFromBackOfProp(
                  propPosition,
                  roomProps,
                  propId,
                  propCatalog,
                  mergedPropLibrary,
                  loadItems,
                  loadType,
                  loadDescriptor,
                  room.level.id,
                  room,
                  outline,
                  mirror,
                );
                break;
              }
              case LegacyPropPositionType.ROOM_WIDTH_PARAM_AT_PROP_AXIS: {
                await pointOnPropRoomAxis(
                  propPosition,
                  roomProps,
                  propId,
                  propCatalog,
                  mergedPropLibrary,
                  outline,
                  loadItems,
                  loadType,
                  loadDescriptor,
                  room.level.id,
                  room,
                );
                break;
              }
            }
            break;
          }
          case LegacyElectricalPositionType.REF_GEOM: {
            const { poly, edgeId, param, paramNormalized, reverse, edgeIds } = queryPt;
            if (!poly) {
              console.warn("load placement edge not found");
              continue;
            }

            const edgeLength = polyLine2Length(poly);
            const edgeIndex = edgeId && edgeIds ? edgeIds.indexOf(edgeId) : 0;

            const [x, y] = reverse
              ? polyLine2EdgeEval(poly, edgeIndex, reverseParam(param, paramNormalized, edgeLength), paramNormalized)
              : polyLine2EdgeEval(poly, edgeIndex, param, paramNormalized);
            const [nX, nY] = polyLine2EdgeNormal(poly, edgeIndex, true);
            loadItems.push({
              loadType: loadType,
              loadDescriptor: loadDescriptor,
              position: [x, y],
              normal: [nX, nY],
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
            });

            break;
          }
          case LegacyElectricalPositionType.ROOM_EDGE: {
            const { roomEdgePosition } = queryPt;
            const { edgeId, param, paramNormalized, reverse } = roomEdgePosition.point;

            const edgeIndex = edgeIds.indexOf(edgeId);
            console.log({ edgeIndex, edgeId });
            const edge = polyLine2Edge(outline, edgeIndex);
            const edgeLength = polyLine2EdgeLength(outline, edgeIndex);

            switch (roomEdgePosition.type) {
              case LegacyRoomEdgePositionType.ON_EDGE: {
                const [x, y] = reverse
                  ? polyLine2EdgeEval(
                      edge,
                      edgeIndex,
                      reverseParam(param, paramNormalized, edgeLength),
                      paramNormalized,
                    )
                  : polyLine2EdgeEval(edge, edgeIndex, param, paramNormalized);
                const [nX, nY] = polyLine2EdgeNormal(outline, edgeIndex, true);

                loadItems.push({
                  loadType: loadType,
                  loadDescriptor: loadDescriptor,
                  position: [x, y],
                  normal: [nX, nY],
                  levelId: room.level.id,
                  sourceRoomHandle: room.roomHandle,
                });
                break;
              }
              case LegacyRoomEdgePositionType.EDGE_OFFSET: {
                const { distance } = roomEdgePosition;

                const [x, y] = reverse
                  ? polyLine2EdgeEvalNegatedOffset(
                      outline,
                      edgeIndex,
                      distance,
                      reverseParam(param, paramNormalized, edgeLength),
                      paramNormalized,
                    )
                  : polyLine2EdgeEvalNegatedOffset(outline, edgeIndex, distance, param, paramNormalized);
                const [nX, nY] = polyLine2EdgeNormal(outline, edgeIndex, true);

                loadItems.push({
                  loadType: loadType,
                  loadDescriptor: loadDescriptor,
                  position: [x, y],
                  normal: [nX, nY],
                  levelId: room.level.id,
                  sourceRoomHandle: room.roomHandle,
                });
                break;
              }
              case LegacyRoomEdgePositionType.CENTER_LINE: {
                const [pX, pY] = reverse
                  ? polyLine2EdgeEval(
                      outline,
                      edgeIndex,
                      reverseParam(param, paramNormalized, edgeLength),
                      paramNormalized,
                    )
                  : polyLine2EdgeEval(outline, edgeIndex, param, paramNormalized);
                const [oX, oY] = ensureDefined(
                  reverse
                    ? polyLine2OppositePt(
                        outline,
                        edgeIndex,
                        reverseParam(param, paramNormalized, edgeLength),
                        paramNormalized,
                      )
                    : polyLine2OppositePt(outline, edgeIndex, param, paramNormalized),
                );
                const [x, y] = line2MidPt(pX, pY, oX, oY);
                const normal = ROUND_CEILING_LIGHT_SYMBOLS.has(loadDescriptor.symbol)
                  ? directionAwayFromRoomCentroid(outline, x, y)
                  : [0, 1];

                loadItems.push({
                  loadType: loadType,
                  loadDescriptor: loadDescriptor,
                  position: [x, y],
                  normal: normal,
                  levelId: room.level.id,
                  sourceRoomHandle: room.roomHandle,
                });

                break;
              }
            }

            break;
          }
        }
      }
    }
  }
  return loadItems;
}

async function pointOnProp(
  elecPlacement: LegacyAtProp,
  roomProps: Prop[],
  propId: string,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  loadItems: Load[],
  loadType: string,
  loadDescriptor: LoadDescriptor,
  levelId: string,
  room: Room,
): Promise<void> {
  const { adjustmentNormal, adjustmentTangent } = elecPlacement;
  const propMatches = roomProps.filter((x) => x.src === propId);
  for (const propMatch of propMatches) {
    const { xAdj, yAdj } = await propBBAdjustments(propCatalog, propMatch, mergedPropLibrary);
    const [tX, tY] = vec2Rotate(0, 1, degreeToRadian(propMatch.rotationDegrees));
    const [nX, nY] = vec2Perp(tX, tY);
    const [x, y] = [
      propMatch.x + nX * (adjustmentNormal + xAdj) + tX * adjustmentTangent,
      propMatch.y + nY * adjustmentNormal + (adjustmentTangent + yAdj) + tY * adjustmentTangent,
    ];
    loadItems.push({
      loadType: loadType,
      loadDescriptor: loadDescriptor,
      position: [x, y],
      normal: [-tX, -tY],
      levelId,
      sourceRoomHandle: room.roomHandle,
    });
  }
}

export async function pointOffsetFromBackOfProp(
  elecPlacement: LegacyOffsetFromPropBack,
  roomProps: Prop[],
  propId: string,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  loadItems: Load[],
  loadType: string,
  loadDescriptor: LoadDescriptor,
  levelId: string,
  room: Room,
  roomOutline: PolyLine2,
  mirror: boolean,
): Promise<void> {
  const { adjustmentNormal, adjustmentTangent } = elecPlacement;
  const propMatches = roomProps.filter((x) => x.src === propId);
  const flip = mirror ? -1 : 1;

  for (const prop of propMatches) {
    const [tx, ty] = vec2Rotate(0, flip, degreeToRadian(prop.rotationDegrees));
    const [nx, ny] = vec2Perp(tx, ty);
    const { xAdj } = await propBBAdjustments(propCatalog, prop, mergedPropLibrary);
    const [xWithAdjust, yWithAdjust] = vec2Rotate(xAdj, 0, degreeToRadian(prop.rotationDegrees));
    const [bbx, bby] = [prop.x + xWithAdjust, prop.y + yWithAdjust];

    const position = normalizeEntityPointToPolyLine2({
      outline: roomOutline,
      bbx,
      bby,
      entityPlaneXOffset: adjustmentTangent * flip,
      entityPlaneXNormalizedToRoomWidth: false,
      entityPlaneYOffset: adjustmentNormal,
      entityPlaneYNormalizedToRoomWidth: false,
      tx: tx * prop.scaleY,
      ty: ty * prop.scaleY,
      nx,
      ny,
    });
    if (position) {
      const [x, y] = position.location;
      const [nx, ny] = position.normal;
      const [ox, oy] = [nx, ny];
      const [rnx, rny] = mirror ? vec2Rotate(ox, oy, degreeToRadian(180)) : [nx, ny];
      loadItems.push({
        loadType: loadType,
        loadDescriptor: loadDescriptor,
        position: [x, y],
        normal: [rnx, rny],
        levelId,
        sourceRoomHandle: room.roomHandle,
      });
    }
  }
}

async function pointOnPropRoomAxis(
  elecPlacement: LegacyRoomWidthParamAtPropAxis,
  roomProps: Prop[],
  propId: string,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  outline: PolyLine2,
  loadItems: Load[],
  loadType: string,
  loadDescriptor: LoadDescriptor,
  levelId: string,
  room: Room,
): Promise<void> {
  const { adjustmentNormal, param: paramOnAxis, paramNormalized, reverse } = elecPlacement;
  const propMatches = roomProps.filter((x) => x.src === propId);
  for (const propMatch of propMatches) {
    const [tx, ty] = vec2Rotate(0, 1, degreeToRadian(propMatch.rotationDegrees));
    const [nx, ny] = vec2Perp(tx, ty);
    const { xAdj, yAdj } = await propBBAdjustments(propCatalog, propMatch, mergedPropLibrary);
    const [xWithAdjust, yWithAdjust] = vec2Rotate(xAdj, -yAdj, degreeToRadian(propMatch.rotationDegrees));
    const [bbx, bby] = [propMatch.x + xWithAdjust, propMatch.y + yWithAdjust];
    const position = normalizeEntityPointToPolyLine2ForceXToRoomWidth({
      outline: outline,
      bbx,
      bby,
      entityPlaneXOffset: paramOnAxis,
      entityPlaneXNormalizedToRoomWidth: paramNormalized,
      entityPlaneYOffset: adjustmentNormal,
      entityPlaneYNormalizedToRoomWidth: false,
      tx,
      ty,
      nx,
      ny,
      reverse: !reverse,
    });

    if (position) {
      const [x, y] = position.location;
      const [normx, normy] = position.normal;
      loadItems.push({
        loadType: loadType,
        loadDescriptor: loadDescriptor,
        position: [x, y],
        normal: [normx, normy],
        levelId,
        sourceRoomHandle: room.roomHandle,
      });
    }
  }
}
